// client/src/components/LogIn.js
import React, { useState, useContext } from 'react'
import { Formik } from 'formik'
import {
  Button,
  Card,
  Container,
  Form,
  InputGroup,
  Spinner,
} from 'react-bootstrap'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { UserContext } from '../context'
import { showErrorNotification } from '../services/notificationService'
import * as Yup from 'yup'
import queryString from 'query-string'
import { BsPerson, BsLock, BsEye, BsEyeSlash } from 'react-icons/bs'

function LogIn() {
  const [isSubmitted, setSubmitted] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [showRecoverPass, setShowRecoverPass] = useState(false)
  const [showPw, setShowPw] = useState(false)

  const [state, , actions] = useContext(UserContext)

  const location = useLocation()
  const { redirect } = queryString.parse(location.search)

  const isLoggedIn = !!state.auth

  const onSubmit = async (values) => {
    setLoading(true)
    try {
      const { ok, error } = await actions.logIn(values.username, values.password)
      if (!ok) {
        showErrorNotification(error)
        setShowRecoverPass(true)
      } else {
        // UserProvider already set auth, user, ready
        setSubmitted(true)
      }
    } catch (err) {
      console.error(err)
      setShowRecoverPass(true)
    } finally {
      setLoading(false)
    }
  }

  // Validaciones
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const usernameRegex = /^[a-zA-Z0-9._]{3,30}$/

  const loginSchema = Yup.object().shape({
    username: Yup.string()
      .trim()
      .matches(/\S/, 'El campo no debe estar vacío')
      .required('El usuario/email es obligatorio')
      .test(
        'is-valid-user-or-email',
        'Ingresa un usuario o correo válido',
        (v) => emailRegex.test(v || '') || usernameRegex.test(v || '')
      ),
    password: Yup.string().required('La contraseña es obligatoria'),
  })

  // Redirige si ya hay sesión lista, o tras submit exitoso
  if ((state.ready && isLoggedIn) || isSubmitted) {
    return <Navigate to={redirect || '/'} replace />
  }

  return (
    <Container className='mt-2 centered-container'>
      <Card className='login-card'>
        <Card.Header className='login-card__header'>Inicia Sesión</Card.Header>
        <Card.Body>
          <Formik
            initialValues={{ username: '', password: '' }}
            validationSchema={loginSchema}
            onSubmit={onSubmit}
          >
            {({
              errors,
              handleChange,
              handleSubmit,
              handleBlur,
              touched,
              values,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                {/* Usuario / Email */}
                <Form.Group className='mb-3' controlId='username'>
                  <Form.Label>Usuario/Email:</Form.Label>
                  <InputGroup hasValidation>
                    <InputGroup.Text className='login-input__icon'>
                      <BsPerson />
                    </InputGroup.Text>
                    <Form.Control
                      name='username'
                      autoComplete='username'
                      placeholder='Tu usuario o correo'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.username}
                      isInvalid={touched.username && !!errors.username}
                    />
                    <Form.Control.Feedback type='invalid'>
                      {errors.username}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>

                {/* Password */}
                <Form.Group className='mb-3' controlId='password'>
                  <Form.Label>Contraseña:</Form.Label>
                  <InputGroup hasValidation>
                    <InputGroup.Text className='login-input__icon'>
                      <BsLock />
                    </InputGroup.Text>
                    <Form.Control
                      name='password'
                      type={showPw ? 'text' : 'password'}
                      autoComplete='current-password'
                      placeholder='Tu contraseña'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.password}
                      isInvalid={touched.password && !!errors.password}
                    />
                    <Button
                      type='button'
                      variant='outline-secondary'
                      className='login-input__toggle'
                      onClick={() => setShowPw((p) => !p)}
                      aria-label={showPw ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showPw ? <BsEyeSlash /> : <BsEye />}
                    </Button>
                    <Form.Control.Feedback type='invalid'>
                      {errors.password}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>

                {/* Submit */}
                <div className='d-grid mb-3'>
                  <Button type='submit' variant='primary' size='lg' disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Spinner
                          as='span'
                          animation='border'
                          size='sm'
                          role='status'
                          aria-hidden='true'
                          className='me-2'
                        />
                        Iniciando…
                      </>
                    ) : (
                      'Inicia Sesión'
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>

          <Card.Text className='text-center mb-0'>
            ¿No tienes cuenta?{' '}
            {redirect ? (
              <Link to={{ pathname: '/sign-up', search: '?redirect=/payment-methods' }}>
                Crear Cuenta
              </Link>
            ) : (
              <Link to='/sign-up'>Crear Cuenta</Link>
            )}
          </Card.Text>

          {showRecoverPass && (
            <Card.Text className='text-center mt-2'>
              ¿Olvidaste tu contraseña? <Link to='/password-recovery'>Recupérala</Link>
            </Card.Text>
          )}
        </Card.Body>
      </Card>
    </Container>
  )
}

export default LogIn
